import type { CSSProperties } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, Zap } from 'lucide-react';
import {
  ChartEmpty,
  ChartTooltip,
  MilestoneGantt,
  MilestoneLegend,
  PostureStrip,
  SeverityBar,
  SeverityByDomain,
  SeverityLegend,
  TrendChart,
  TrendLegend,
  useChartTooltip,
} from '@/components/ui/charts';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils/cn';
import { formatDayMonth } from '@/lib/utils/format';
import type { HealthReadinessView } from '@/lib/api/health-readiness';
import type { ScheduleMilestone } from '@/lib/api/health-schedule';
import { POSTURE_LABEL, plural, sameDomainName, scoreBand } from '../model';
import { useHrNavigate } from '../navigation';
import {
  categoryPoints,
  formatChange,
  formatPoints,
  largestGaps,
  localDayIso,
  openFindingsOf,
  pendingReadings,
  readinessTrendPoints,
  severityRows,
  toGanttMilestone,
} from './overview-model';
import { CARD_LINK, CardMeta, CountUp, OVERLINE, OverviewCard } from './parts';

/**
 * The Overview's cards below the verdicts (prototype `aiCallout()`,
 * `getStarted()`, `trendCard()`, `pointsCard()`, `domainMap()`,
 * `roadCard()`, `findingsCard()`), drawn with the chart primitives.
 * Each card names where its numbers come from in the composite read or
 * the schedule list; nothing here adds anything up.
 */

type CardProps = { engagementId: string; view: HealthReadinessView };

const cssI = (i: number) => ({ '--i': i }) as CSSProperties;

// ---------------------------------------------------------------------------
// AI readings waiting
// ---------------------------------------------------------------------------

export function AiCallout({ engagementId, view }: CardProps) {
  const go = useHrNavigate(engagementId);
  const { domains, criteria } = view.aiPending;
  const readings = pendingReadings(view);
  if ((domains === 0 && criteria === 0) || readings.length === 0) return null;
  const what = [
    domains > 0 ? plural(domains, 'domain rating', 'domain ratings') : null,
    criteria > 0 ? plural(criteria, 'criterion score', 'criterion scores') : null,
  ]
    .filter(Boolean)
    .join(' and ');
  return (
    <section
      aria-label="AI readings waiting"
      className="flex flex-wrap items-center gap-3 rounded-card border-l-2 border-ai-surface-border bg-ai-surface px-3.5 py-2.5 text-body-sm text-ai-ink"
    >
      <span className="inline-flex items-center gap-1.5 font-semibold">
        <Zap className="h-3.5 w-3.5" aria-hidden="true" />
        The AI has {what} waiting on this screen
      </span>
      {readings.map((r) => (
        <button
          key={`${r.kind}-${r.id}`}
          type="button"
          onClick={() =>
            go(r.kind === 'domain' ? { tab: 'domains', id: r.id } : { tab: 'criteria', id: r.id })
          }
          className="inline-flex h-[26px] items-center gap-1.5 rounded-full border border-ai-surface-border bg-paper-0 pl-2.5 pr-0.5 text-meta font-medium text-ai-ink hover:border-ai-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          {r.name}: {r.reading}
          <AiConfidenceChip value={r.confidence} className="h-5" />
        </button>
      ))}
      <span className="ml-auto text-meta text-ink-500">
        You confirm or rate differently; nothing changes until you do.
      </span>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Get this engagement assessed (new engagement)
// ---------------------------------------------------------------------------

export function GetStarted({ engagementId, view }: CardProps) {
  const readiness = view.verdict.readiness;
  const counts = view.verdict.posture.counts;
  const rated = view.domains.filter((d) => d.rating !== null).length;
  const aiRead = view.aiPending.domains > 0 || view.aiPending.criteria > 0;
  const firstDomain = view.domains[0]?.id;
  const firstCriterion = view.criteria[0]?.id;
  const params = { engagementId };

  const steps = [
    {
      title: 'Add the project documents',
      sub: 'The AI reads them and proposes ratings and scores.',
      done: aiRead ? 1 : 0,
      of: 1,
      link: (
        <Link to="/engagements/$engagementId/documents" params={params} className={CARD_LINK}>
          Open Documents →
        </Link>
      ),
    },
    {
      title: 'Rate the domains',
      sub: `${rated} of ${counts.total} rated`,
      done: rated,
      of: counts.total,
      link: firstDomain ? (
        <Link
          to="/engagements/$engagementId/health-readiness/domains/$domainId"
          params={{ engagementId, domainId: firstDomain }}
          className={CARD_LINK}
        >
          Open →
        </Link>
      ) : null,
    },
    ...(readiness
      ? [
          {
            title: 'Score the go-live criteria',
            sub: `${readiness.scoredCount} of ${readiness.criteriaCount} scored`,
            done: readiness.scoredCount,
            of: readiness.criteriaCount,
            link: firstCriterion ? (
              <Link
                to="/engagements/$engagementId/health-readiness/criteria/$criterionId"
                params={{ engagementId, criterionId: firstCriterion }}
                className={CARD_LINK}
              >
                Open →
              </Link>
            ) : null,
          },
        ]
      : []),
    {
      title: 'Capture the first snapshot',
      sub: 'It freezes the day; the trend starts here.',
      done: view.reports.snapshots.count > 0 ? 1 : 0,
      of: 1,
      link: (
        <Link
          to="/engagements/$engagementId/health-readiness/snapshots"
          params={params}
          className={CARD_LINK}
        >
          Open →
        </Link>
      ),
    },
  ];

  return (
    <OverviewCard
      title="Get this engagement assessed"
      aside={<CardMeta>The charts on this screen fill in as you go</CardMeta>}
    >
      <ol
        className={cn(
          'grid gap-3 sm:grid-cols-2',
          steps.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3',
        )}
      >
        {steps.map((s, i) => {
          const complete = s.of > 0 && s.done >= s.of;
          return (
            <li
              key={s.title}
              className="a-rise flex flex-col gap-2 rounded-card border border-ink-100 bg-paper-0 p-3.5"
              style={cssI(i)}
            >
              <span
                className={cn(
                  'inline-flex h-6 w-6 items-center justify-center rounded-full text-meta font-semibold',
                  complete ? 'bg-posture-healthy text-paper-0' : 'bg-paper-100 text-ink-700',
                )}
              >
                {complete ? <Check className="h-3.5 w-3.5" aria-label="Done" /> : i + 1}
              </span>
              <span className="text-body-sm font-semibold text-ink-900">{s.title}</span>
              <span className="text-meta text-ink-500">{s.sub}</span>
              <span
                role="img"
                aria-label={`${s.done} of ${s.of}`}
                className="relative block h-2 overflow-hidden rounded bg-ink-100"
              >
                <i
                  className="a-grow absolute inset-y-0 left-0 rounded-r-[3px] bg-ink-700"
                  style={{ ...cssI(i), width: `${s.of > 0 ? (s.done / s.of) * 100 : 0}%` }}
                />
              </span>
              {s.link}
            </li>
          );
        })}
      </ol>
    </OverviewCard>
  );
}

// ---------------------------------------------------------------------------
// Readiness over time
// ---------------------------------------------------------------------------

export function TrendCard({ engagementId, view, today }: CardProps & { today: Date }) {
  const readiness = view.verdict.readiness;
  if (!readiness) return null;
  const points = readinessTrendPoints(view, today);
  const live = points.some((p) => p.live);
  const first = view.trend.find((t) => t.totalPercent !== null);
  const meta = [
    `${plural(view.trend.length, 'snapshot', 'snapshots')}${live ? ' + today' : ''}`,
    view.trendChange !== null && first
      ? `${formatChange(view.trendChange)} points since ${formatDayMonth(first.capturedAt)}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <OverviewCard title="Readiness over time" aside={<CardMeta>{meta}</CardMeta>}>
      <TrendChart
        motionKey="overview"
        points={points}
        thresholds={readiness.thresholds}
        height={300}
      />
      <TrendLegend>
        <Link
          to="/engagements/$engagementId/health-readiness/snapshots"
          params={{ engagementId }}
          className={CARD_LINK}
        >
          Snapshots &amp; trend →
        </Link>
      </TrendLegend>
    </OverviewCard>
  );
}

// ---------------------------------------------------------------------------
// Where the points are
// ---------------------------------------------------------------------------

export function PointsCard({ engagementId, view }: CardProps) {
  const go = useHrNavigate(engagementId);
  const { tip, bind } = useChartTooltip();
  const readiness = view.verdict.readiness;
  if (!readiness) return null;
  const rows = categoryPoints(view.criteriaGroups);
  const maxAvailable = Math.max(1, ...rows.map((r) => r.available));
  const gaps = largestGaps(view.criteria);
  const total = readiness.scoredCount > 0 ? readiness.totalPercent : null;

  return (
    <OverviewCard
      title="Where the points are"
      aside={<CardMeta>earned of available, by category</CardMeta>}
    >
      <div className="mb-3 flex items-baseline gap-2">
        <span className="text-[28px] font-semibold leading-8 text-ink-900">
          {Math.round(total ?? 0)}
        </span>
        <span className="text-meta text-ink-500">
          of 100 points{total !== null ? ` · Go needs ${readiness.thresholds.go}` : ''}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        {rows.map((r, i) => (
          <button
            key={r.category}
            type="button"
            disabled={!r.firstCriterionId}
            onClick={() => r.firstCriterionId && go({ tab: 'criteria', id: r.firstCriterionId })}
            aria-label={`${r.category}: ${formatPoints(r.earned)} of ${formatPoints(r.available)} points`}
            className="-mx-1.5 grid grid-cols-[84px_1fr_74px] items-center gap-2.5 rounded-[6px] px-1.5 py-1.5 text-left text-body-sm font-medium text-ink-900 hover:bg-paper-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
            {...bind({
              title: r.category,
              value: `${formatPoints(r.earned)} of ${formatPoints(r.available)} points`,
              sub: `${formatPoints(r.available - r.earned)} points short`,
            })}
          >
            <span className="truncate">{r.category}</span>
            <span>
              <span
                className="relative block h-3 rounded-r bg-ink-100"
                style={{ width: `${(r.available / maxAvailable) * 100}%` }}
              >
                <i
                  className="a-grow absolute inset-y-0 left-0 rounded-r bg-ink-700"
                  style={{ ...cssI(i), width: `${Math.min(100, (r.earned / r.available) * 100)}%` }}
                />
              </span>
            </span>
            <span className="text-right text-meta font-semibold tabular-nums text-ink-900">
              {r.earned.toFixed(1)}{' '}
              <span className="font-normal text-ink-500">/ {formatPoints(r.available)}</span>
            </span>
          </button>
        ))}
      </div>
      {gaps.length ? (
        <div className="mt-3 flex flex-col gap-1.5 border-t border-ink-100 pt-3">
          <span className={OVERLINE}>Largest gaps</span>
          {gaps.map(({ criterion: c, missing }) => (
            <div key={c.id} className="flex items-center gap-2 text-body-sm text-ink-700">
              <span
                aria-hidden="true"
                className={cn(
                  'h-2.5 w-2.5 flex-none rounded-[3px]',
                  BAND_SWATCH[scoreBand(c.scorePercent, readiness.thresholds)],
                )}
              />
              <Link
                to="/engagements/$engagementId/health-readiness/criteria/$criterionId"
                params={{ engagementId, criterionId: c.id }}
                className="font-medium text-ink-900 no-underline hover:underline hover:underline-offset-[3px]"
              >
                {c.name}
              </Link>
              <span className="text-meta text-ink-500">
                {Math.round(c.scorePercent ?? 0)}% · weight {Math.round(c.weight * 100)}%
              </span>
              <span className="ml-auto font-mono text-meta font-semibold text-ink-900">
                +{missing.toFixed(1)}
              </span>
            </div>
          ))}
          <span className="text-meta text-ink-500">Points each would add at 100%.</span>
        </div>
      ) : null}
      <ChartTooltip tip={tip} />
    </OverviewCard>
  );
}

const BAND_SWATCH: Record<PostureStatus, string> = {
  healthy: 'bg-posture-healthy',
  'at-risk': 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
  unassessed: 'bg-ink-200',
};

// ---------------------------------------------------------------------------
// Domain health
// ---------------------------------------------------------------------------

const TILE_RULE: Record<PostureStatus, string> = {
  critical: 'bg-posture-critical',
  'at-risk': 'bg-posture-at-risk',
  healthy: 'bg-posture-healthy',
  unassessed: 'bg-ink-200',
};

export function DomainMap({ engagementId, view }: CardProps) {
  const go = useHrNavigate(engagementId);
  const snapshots = view.trend.length;
  return (
    <OverviewCard
      title="Domain health"
      aside={
        <CardMeta>
          {snapshots
            ? `rating at each of the last ${plural(snapshots, 'snapshot', 'snapshots')}, then today · `
            : ''}
          open a domain to rate it
        </CardMeta>
      }
    >
      {view.domains.length === 0 ? (
        <ChartEmpty title="No domains yet">
          Add the domains this engagement is assessed on.
        </ChartEmpty>
      ) : (
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 xl:grid-cols-5">
          {view.domains.map((d, i) => {
            const open = openFindingsOf(view, d.id);
            const total = open?.total ?? d.openFindings;
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => go({ tab: 'domains', id: d.id })}
                data-testid={`domain-tile-${d.id}`}
                className="a-rise relative flex min-w-0 flex-col gap-2.5 overflow-hidden rounded-card border border-ink-100 bg-paper-0 pb-2.5 pl-3.5 pr-3 pt-3 text-left transition-[border-color,box-shadow,transform] duration-[140ms] hover:-translate-y-px hover:border-ink-200 hover:shadow-modal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
                style={cssI(i)}
              >
                <span
                  aria-hidden="true"
                  className={cn('absolute inset-y-0 left-0 w-1', TILE_RULE[d.status])}
                />
                <span className="flex items-start justify-between gap-1.5">
                  <span className="min-h-9 text-body-sm font-semibold leading-[18px] text-ink-900">
                    {d.name}
                  </span>
                  <PostureChip
                    status={d.status}
                    dot
                    aria-label={`Rating: ${POSTURE_LABEL[d.status]}`}
                  >
                    {POSTURE_LABEL[d.status]}
                  </PostureChip>
                </span>
                <PostureStrip
                  label={d.name}
                  current={d.status}
                  cells={d.history.map((h) => ({ date: h.capturedAt, posture: h.status }))}
                />
                <span className="flex items-center gap-2 text-meta text-ink-500">
                  {total > 0 ? (
                    <>
                      <span>{plural(total, 'open finding', 'open findings')}</span>
                      {open ? (
                        <span className="max-w-[70px] flex-1">
                          <SeverityBar
                            counts={{
                              critical: open.critical,
                              high: open.high,
                              medium: open.medium,
                              low: open.low,
                            }}
                            label={d.name}
                          />
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span>No open findings</span>
                  )}
                  {d.aiProposal ? (
                    <span
                      className="hr-ai-pulse ml-auto inline-flex items-center gap-1 rounded-full border border-ai-surface-border bg-ai-surface px-1.5 text-[10px] font-semibold leading-4 text-ai-ink"
                      aria-label="AI reading waiting"
                    >
                      <Zap className="h-2.5 w-2.5" aria-hidden="true" />
                      AI
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </OverviewCard>
  );
}

// ---------------------------------------------------------------------------
// Road to go-live
// ---------------------------------------------------------------------------

export function RoadCard({
  engagementId,
  view,
  milestones,
  loading,
  today,
}: CardProps & { milestones: ScheduleMilestone[]; loading: boolean; today: Date }) {
  const schedule = view.domains.find((d) => sameDomainName(d.name, 'Schedule'));
  return (
    <OverviewCard title="Road to go-live" aside={<MilestoneLegend />}>
      {loading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading milestones…
        </div>
      ) : (
        <MilestoneGantt
          motionKey="overview"
          compact
          today={localDayIso(today)}
          milestones={milestones.map(toGanttMilestone)}
        />
      )}
      {milestones.length && schedule ? (
        <div className="mt-2">
          <Link
            to="/engagements/$engagementId/health-readiness/domains/$domainId"
            params={{ engagementId, domainId: schedule.id }}
            className={CARD_LINK}
          >
            Schedule domain →
          </Link>
        </div>
      ) : null}
    </OverviewCard>
  );
}

// ---------------------------------------------------------------------------
// Open findings
// ---------------------------------------------------------------------------

export function FindingsCard({ engagementId, view }: CardProps) {
  const go = useHrNavigate(engagementId);
  const open = view.evidence.openFindings;
  const bySeverity = view.evidence.openBySeverity;
  const link = (
    <Link
      to="/engagements/$engagementId/health-readiness/findings"
      params={{ engagementId }}
      className={CARD_LINK}
    >
      All findings →
    </Link>
  );
  if (open === 0) {
    const none = (view.evidence.resolvedFindings ?? 0) === 0;
    return (
      <OverviewCard title="Open findings" aside={none ? null : link}>
        <ChartEmpty title={none ? 'No findings yet' : 'No open findings'}>
          Findings come from interviews, document reviews and your own observations.
        </ChartEmpty>
      </OverviewCard>
    );
  }
  return (
    <OverviewCard title="Open findings" aside={link}>
      <div className="mb-3.5 flex items-center gap-4">
        <span
          className="text-[34px] font-semibold leading-10 text-ink-900"
          data-testid="open-findings-total"
        >
          <CountUp value={open} motionKey="overview" />
        </span>
        {bySeverity ? (
          <div className="flex flex-1 flex-col gap-2">
            <SeverityBar counts={bySeverity} size="lg" />
            <SeverityLegend counts={bySeverity} />
          </div>
        ) : null}
      </div>
      <div className={cn(OVERLINE, 'mb-1.5')}>By domain</div>
      <SeverityByDomain
        rows={severityRows(view)}
        limit={5}
        onSelect={(id, severity) =>
          go({ tab: 'findings', filters: { domain: id || undefined, severity } })
        }
        renderName={(row) =>
          row.id ? (
            <Link
              to="/engagements/$engagementId/health-readiness/findings"
              params={{ engagementId }}
              search={{ domain: row.id }}
              className="text-inherit no-underline hover:underline hover:underline-offset-[3px]"
            >
              {row.name}
            </Link>
          ) : (
            row.name
          )
        }
      />
    </OverviewCard>
  );
}
